import pixelmatch from 'pixelmatch';
import { RenderedPage } from './render-pdf';

export interface PageComparison {
	pageNumber: number;
	diffPixels: number;
	totalPixels: number;
	diffPercentage: number;
	diffCanvas: HTMLCanvasElement;
	page1Canvas: HTMLCanvasElement;
	page2Canvas: HTMLCanvasElement;
	hasDifference: boolean;
	hasPage1: boolean;
	hasPage2: boolean;
}

export interface PdfComparison {
	orgId: string;
	pdfType: string;
	clientName: string;
	settingsPart: string;
	pages: PageComparison[];
	totalDiffPixels: number;
	hasAnyDifference: boolean;
	maxPages: number;
	comparisonTimeMs: number;
}

/**
 * Compares two sets of rendered PDF pages using pixelmatch
 */
export const comparePdfPages = (
	pages1: RenderedPage[],
	pages2: RenderedPage[],
	orgId: string,
	pdfType: string,
	clientName: string,
	settingsPart: string,
	threshold: number = 0.1
): PdfComparison => {
	const startTime = performance.now();
	const maxPages = Math.max(pages1.length, pages2.length);
	const pageComparisons: PageComparison[] = [];
	let totalDiffPixels = 0;

	for (let i = 0; i < maxPages; i++) {
		const page1 = pages1[i];
		const page2 = pages2[i];

		// If one PDF has more pages than the other
		if (!page1 || !page2) {
			const existingPage = page1 || page2;
			const diffCanvas = document.createElement('canvas');
			diffCanvas.width = existingPage.width;
			diffCanvas.height = existingPage.height;
			const ctx = diffCanvas.getContext('2d');
			if (ctx) {
				// Fill with red to indicate missing page
				ctx.fillStyle = 'rgba(255, 0, 0, 0.3)';
				ctx.fillRect(0, 0, diffCanvas.width, diffCanvas.height);
			}

			// Create placeholder canvas for missing page
			const placeholderCanvas = document.createElement('canvas');
			placeholderCanvas.width = existingPage.width;
			placeholderCanvas.height = existingPage.height;

			pageComparisons.push({
				pageNumber: i + 1,
				diffPixels: existingPage.width * existingPage.height,
				totalPixels: existingPage.width * existingPage.height,
				diffPercentage: 100,
				diffCanvas,
				page1Canvas: page1?.canvas || placeholderCanvas,
				page2Canvas: page2?.canvas || placeholderCanvas,
				hasDifference: true,
				hasPage1: !!page1,
				hasPage2: !!page2,
			});

			totalDiffPixels += existingPage.width * existingPage.height;
			continue;
		}

		// Ensure both pages have the same dimensions
		const width = Math.max(page1.width, page2.width);
		const height = Math.max(page1.height, page2.height);

		// If dimensions don't match, create normalized canvases
		let img1 = page1.imageData;
		let img2 = page2.imageData;

		if (page1.width !== width || page1.height !== height || page2.width !== width || page2.height !== height) {
			// Create normalized canvases
			const canvas1 = document.createElement('canvas');
			canvas1.width = width;
			canvas1.height = height;
			const ctx1 = canvas1.getContext('2d');
			ctx1?.drawImage(page1.canvas, 0, 0);
			img1 = ctx1!.getImageData(0, 0, width, height);

			const canvas2 = document.createElement('canvas');
			canvas2.width = width;
			canvas2.height = height;
			const ctx2 = canvas2.getContext('2d');
			ctx2?.drawImage(page2.canvas, 0, 0);
			img2 = ctx2!.getImageData(0, 0, width, height);
		}

		// Create diff canvas
		const diffCanvas = document.createElement('canvas');
		diffCanvas.width = width;
		diffCanvas.height = height;
		const diffCtx = diffCanvas.getContext('2d');
		const diffImageData = diffCtx!.createImageData(width, height);

		// Compare pixels
		const diffPixels = pixelmatch(
			img1.data,
			img2.data,
			diffImageData.data,
			width,
			height,
			{ threshold }
		);

		diffCtx!.putImageData(diffImageData, 0, 0);

		const totalPixels = width * height;
		const diffPercentage = (diffPixels / totalPixels) * 100;

		pageComparisons.push({
			pageNumber: i + 1,
			diffPixels,
			totalPixels,
			diffPercentage,
			diffCanvas,
			page1Canvas: page1.canvas,
			page2Canvas: page2.canvas,
			hasDifference: diffPixels > 0,
			hasPage1: true,
			hasPage2: true,
		});

		totalDiffPixels += diffPixels;
	}

	const endTime = performance.now();
	const comparisonTimeMs = endTime - startTime;

	return {
		orgId,
		pdfType,
		clientName,
		settingsPart,
		pages: pageComparisons,
		totalDiffPixels,
		hasAnyDifference: totalDiffPixels > 0,
		maxPages,
		comparisonTimeMs,
	};
};
